/* feature:slowmo — Zeitlupe & Regie-Kamera (client side).
   Time control: stepless 0.1x–4x slider in the battle bar, keys [ / ] (slower / faster, also - / +), Backspace = 1x.
   The worker only changes how many fixed ticks run per real second (src/sim/slowmoPacer.js) and lowers the scale on
   overload (reported as 'slowmoCap'); a tick is always 1/30 s of game time, so replays do not depend on the speed.
   Multiplayer is the exception: setLockstep(true), the host's speed command scales the tick there (see sim.worker.js). Bullet time: big moments (Atombombe, Sprengfass in der Menge, a giant falls) briefly
   drop to 0.25x and ease back. Regie: the Kino camera cuts to the most exciting spots (src/features/slowmoDirector.js).
   Client animation runs on game time: frame() returns the scaled dt for uTime, now() is the game clock for effects.

   API for other modules (e.g. a replay system): getSlowmo() -> {
     setTimeScale(v), getTimeScale(), getEffectiveTimeScale(), getGameTime(), now(), rate(),
     setDirector(on), isDirecting(), getShot(), focusHighlight(h),
     getHighlights({ since, minWeight }), onHighlight(cb) -> unsubscribe,
     setBulletTime(on), isBulletTime(), triggerBulletTime(), setLockstep(on), isLocked() } */
import './slowmo.css';
import { SLOWMO_MIN, SLOWMO_MAX } from '../sim/slowmoPacer.js';
import { BULLET_DURATION, bulletEnvelope, bulletScale, createDirector, createHighlightLog, estimateFront, scanEvents } from './slowmoDirector.js';

const STEPS = [0.1, 0.15, 0.2, 0.25, 0.35, 0.5, 0.75, 1, 1.5, 2, 3, 4];
const LOG_SPAN = Math.log(SLOWMO_MAX / SLOWMO_MIN);
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const nf = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
export const fmtScale = s => nf.format(s) + '×';
const toSlider = s => Math.round(Math.log(s / SLOWMO_MIN) / LOG_SPAN * 1000);
// slider position -> scale; snaps onto the round steps nearby so 1x is easy to hit
function fromSlider(v) {
  const s = SLOWMO_MIN * Math.exp(v / 1000 * LOG_SPAN);
  for (const p of STEPS) if (Math.abs(s / p - 1) < 0.04) return p;
  return Math.round(s * 100) / 100;
}
const store = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };
const load = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };

let instance = null;
/** the running instance (null before main.js created it) */
export const getSlowmo = () => instance;

/**
 * ctx (from main.js): worker, camGoal, camCur, toast(msg), ground(x, z), blocked(),
 *   state() -> { simState, paused, timeScale, kino, possessed, gen, N, split }, bufs() -> [prevB, curB], armies(),
 *   setTimeScale(v) (sets main's timeScale and sends 'run'), setKino(on)
 */
export function createSlowmo(ctx) {
  const $ = s => document.querySelector(s);
  let user = 1, cap = SLOWMO_MAX, locked = false, sent = 1, sentAt = 0;
  let btOn = load('aw-slowmo-bullet', '1') === '1', btStart = -1e9, btUntil = -1e9;
  let regie = false, capWarned = SLOWMO_MAX;
  let clockMs = 1000;
  let lastGen = -1, lastT = 0, prevT = 0, arrival = 0, frameMs = 1000 / 30;
  let front = null, frontAt = 0, contactSeen = false, uiAt = 0, capT = 0, shotShown = null;
  const log = createHighlightLog(), director = createDirector(), listeners = new Set();
  const realNow = () => performance.now() / 1000;

  /* ---- UI: slider after the speed presets, Regie + Bullet-Time next to the Kino button, caption ---- */
  const presets = [...document.querySelectorAll('[data-speed]')];
  const box = document.createElement('span');
  box.className = 'slowmo-ctl';
  box.innerHTML = '<input type="range" class="slowmo-range" min="0" max="1000" step="1" aria-label="Spieltempo" title="Spieltempo 0,1× bis 4× · Tasten [ und ] · Rücktaste = 1×"><output class="slowmo-out" aria-live="off">1×</output>';
  (presets[presets.length - 1] || $('#pause')).after(box);
  const range = box.querySelector('.slowmo-range'), out = box.querySelector('.slowmo-out');
  range.value = toSlider(1);
  const kinoBtn = $('#kino');
  const regieBtn = document.createElement('button');
  regieBtn.id = 'slowmoRegie'; regieBtn.setAttribute('aria-pressed', 'false'); regieBtn.textContent = 'Regie';
  regieBtn.title = 'Regie-Kamera: schneidet automatisch zu den spannendsten Stellen';
  const btBtn = document.createElement('button');
  btBtn.id = 'slowmoBullet'; btBtn.setAttribute('aria-pressed', String(btOn)); btBtn.textContent = 'Bullet-Time';
  btBtn.title = 'Bremst bei Atombombe, Sprengfass in der Menge oder dem Tod eines Riesen kurz auf 0,25× ab';
  kinoBtn.after(regieBtn, btBtn);
  const cap$ = document.createElement('div');
  cap$.className = 'slowmo-cap'; cap$.hidden = true;
  document.body.appendChild(cap$);
  let capTimer = 0;
  function caption(text, ms = 2600) {
    cap$.textContent = text; cap$.hidden = false; cap$.classList.add('slowmo-in');
    clearTimeout(capTimer); capTimer = setTimeout(() => { cap$.classList.remove('slowmo-in'); setTimeout(() => { if (!cap$.classList.contains('slowmo-in')) cap$.hidden = true; }, 300); }, ms);
  }

  range.addEventListener('input', () => setUser(fromSlider(+range.value), false));
  presets.forEach(b => b.addEventListener('click', () => setUser(+b.dataset.speed))); // main.js already set timeScale; keep ours in sync
  regieBtn.addEventListener('click', () => setDirector(!(regie && ctx.state().kino)));
  btBtn.addEventListener('click', () => setBulletTime(!btOn));
  addEventListener('keydown', e => {
    if (e.target.matches && e.target.matches('input,select,textarea') && e.target !== range) return;
    if (ctx.blocked() || ctx.state().simState !== 'running') return;
    const k = e.key, c = e.code;
    const slower = k === '[' || k === '-' || c === 'BracketLeft' || c === 'NumpadSubtract';
    const faster = k === ']' || k === '+' || c === 'BracketRight' || c === 'NumpadAdd';
    if (!slower && !faster && c !== 'Backspace') return;
    e.preventDefault();
    if (locked) { ctx.toast('Im Mehrspieler läuft die Zeit für alle gleich schnell.'); return; }
    if (c === 'Backspace') setUser(1);
    else setUser(slower ? STEPS.slice().reverse().find(s => s < user - 1e-6) ?? STEPS[0] : STEPS.find(s => s > user + 1e-6) ?? STEPS[STEPS.length - 1]);
    caption('Tempo ' + fmtScale(user), 1200);
  });

  /* ---- time scale: user value -> bullet time -> overload cap -> main.js timeScale -> worker 'run' ---- */
  function setUser(v, moveSlider = true) {
    if (locked) return;
    user = clamp(+v || 1, SLOWMO_MIN, SLOWMO_MAX);
    if (moveSlider) range.value = toSlider(user);
    presets.forEach(b => b.classList.toggle('on', Math.abs(+b.dataset.speed - user) < 1e-6));
    apply(true); ui(true);
  }
  const btAge = () => realNow() - btStart;
  const btActive = () => btAge() < BULLET_DURATION;
  function desired() {
    if (locked) return 1;
    const s = btActive() ? bulletScale(user, btAge()) : user;
    return Math.round(Math.min(s, cap) * 1000) / 1000;
  }
  function apply(force) {
    const d = desired(), t = realNow();
    if (Math.abs(d - ctx.state().timeScale) < 1e-4) { sent = d; return; }
    if (!force && d !== user && t - sentAt < 0.05) return; // bullet-time ramp: at most 20 'run' messages per second
    sent = d; sentAt = t; ctx.setTimeScale(d);
  }
  function maybeBullet(h) {
    const st = ctx.state(), t = realNow();
    if (!btOn || locked || st.simState !== 'running' || st.paused || t < btUntil || user <= 0.25) return;
    btStart = t; btUntil = t + BULLET_DURATION + 4; // then a short cooldown
    caption('Bullet-Time · ' + h.label, 2200);
  }
  function setBulletTime(on) {
    btOn = !!on; store('aw-slowmo-bullet', btOn ? '1' : '0');
    btBtn.setAttribute('aria-pressed', String(btOn));
    if (!btOn && btActive()) { btStart = -1e9; apply(true); }
  }
  function setDirector(on) {
    regie = !!on;
    if (regie) { ctx.setKino(true); director.reset(); shotShown = null; ctx.toast('Regie-Kamera: schneidet automatisch zu den spannendsten Stellen'); }
    regieBtn.setAttribute('aria-pressed', String(regie));
  }
  function setLockstep(on) {
    locked = !!on;
    ctx.worker.postMessage({ cmd: 'slowmoLock', on: locked });
    range.disabled = locked; btBtn.disabled = locked; // the presets stay: in multiplayer they send the host's speed command
    if (locked) { btStart = -1e9; user = 1; range.value = toSlider(1); }
    apply(true); ui(true);
  }

  /* ---- game clock + interpolation of worker frames ---- */
  const effective = () => Math.max(0.01, ctx.state().timeScale);
  function getGameTime() {
    const a = clamp((performance.now() - arrival) / frameMs, 0, 1);
    return prevT + (lastT - prevT) * a;
  }
  function rate() {
    const st = ctx.state();
    return st.simState === 'running' || st.simState === 'replay' ? (st.paused ? 0 : st.timeScale) : 1;
  }
  function onFrame(m) {
    if (m.gen !== lastGen) { lastGen = m.gen; log.clear(); director.reset(); contactSeen = false; front = null; prevT = lastT = m.t; arrival = performance.now(); }
    const d = m.t - lastT;
    prevT = lastT; lastT = m.t; arrival = performance.now();
    // a frame carries d game seconds; at the current scale they take d / scale real seconds -> butter-smooth at 0.1x
    if (d > 0) frameMs = clamp(d * 1000 / effective(), 8, 1500);
    if (m.nev) {
      const A = ctx.armies(), st = ctx.state(), split = st.split, G = st.GRP;
      const armyOf = i => (G && G.length > i ? A[G[i]] : A[i < split ? 0 : 1]); // the unit's group
      const found = scanEvents(m.ev, m.nev, m.t, { isGiant: i => !!(armyOf(i) && armyOf(i).stats.giant), size: i => armyOf(i) ? armyOf(i).stats.r : 1 });
      for (const h of found) addHighlight(h);
    }
    return frameMs;
  }
  function addHighlight(h) {
    const e = log.add(h);
    if (h.bullet) maybeBullet(e);
    for (const cb of listeners) { try { cb({ ...e }); } catch (err) { console.error(err); } }
  }
  function onCap(c) {
    cap = c; apply(true); ui(true);
    if (cap < user && cap < capWarned && realNow() - capT > 5) { capT = realNow(); ctx.toast(`Überlast: Tempo auf ${fmtScale(cap)} gesenkt`); }
    capWarned = cap;
  }

  /* ---- per frame (main.js frame loop) ---- */
  function frame(dt) {
    if (btActive() || sent !== desired()) apply(!btActive());
    const r = rate();
    clockMs += dt * 1000 * r;
    const t = realNow(), st = ctx.state();
    if (st.simState === 'running' && t - frontAt > 1) {
      frontAt = t;
      const [, cb] = ctx.bufs();
      front = estimateFront(cb, st.N, st.split);
      if (front && front.contact && !contactSeen && st.N) { contactSeen = true; addHighlight({ id: 0, kind: 'contact', label: 'Erster Kontakt', t: lastT, t1: lastT, x: front.x, y: 0, z: front.z, r: front.half, w: 2, bullet: false }); }
    }
    if (regie && !st.kino) setDirector(false);
    regieBtn.hidden = kinoBtn.hidden;
    ui(false);
    return dt * r;
  }
  function ui(force) {
    const t = realNow(); if (!force && t - uiAt < 0.12) return; uiAt = t;
    const eff = desired(), over = !locked && cap < user, bt = btActive() && bulletEnvelope(btAge()) > 0.01;
    out.textContent = over ? `${fmtScale(eff)} · Überlast` : fmtScale(bt ? eff : user);
    out.title = over ? `Der Rechner schafft gerade nur ${fmtScale(cap)}; das Tempo steigt wieder, sobald Luft ist.` : locked ? 'Im Mehrspieler gesperrt' : '';
    out.classList.toggle('slowmo-warn', over); out.classList.toggle('slowmo-bt', bt);
  }

  /* ---- director camera (called from updateCamera while the Kino camera is on) ---- */
  const S = { t: 0, log, front: null, center: { x: 0, z: 0, ext: 60 }, cur: null, ground: ctx.ground, unit: unitAt, hero: heroNear };
  const U = { x: 0, y: 0, z: 0, h: 1, rot: 0, alive: 0 };
  function unitAt(i) {
    const [pb0, cb] = ctx.bufs(), st = ctx.state();
    if (!cb || i < 0 || i >= st.N || cb.length < (i + 1) * 10) return null;
    const pb = pb0 && pb0.length === cb.length ? pb0 : cb, o = i * 10, a = clamp((performance.now() - arrival) / frameMs, 0, 1);
    const A = ctx.armies()[st.GRP && st.GRP.length > i ? st.GRP[i] : i < st.split ? 0 : 1];
    U.x = pb[o] + (cb[o] - pb[o]) * a; U.y = pb[o + 1] + (cb[o + 1] - pb[o + 1]) * a; U.z = pb[o + 2] + (cb[o + 2] - pb[o + 2]) * a;
    U.rot = cb[o + 3]; U.alive = cb[o + 9] > 0 ? 1 : 0; U.h = A ? A.stats.h : 1;
    return U;
  }
  // a unit worth following near (x, z): giants first, then the biggest, then the closest
  function heroNear(x, z, preferGiant) {
    const [, cb] = ctx.bufs(), st = ctx.state(), A = ctx.armies();
    if (!cb || !st.N || !A[0]) return -1;
    const step = Math.max(1, Math.floor(st.N / 3000));
    let best = -1, bs = -Infinity;
    for (let i = 0; i < st.N; i += step) {
      const o = i * 10; if (!cb[o + 9]) continue;
      const B = A[st.GRP && st.GRP.length > i ? st.GRP[i] : i < st.split ? 0 : 1].stats;
      const s = (preferGiant && B.giant ? 1000 : 0) + B.h * 4 - Math.hypot(cb[o] - x, cb[o + 2] - z) * 0.2;
      if (s > bs) { bs = s; best = i; }
    }
    return best;
  }
  function direct(dt, camGoal) {
    const st = ctx.state();
    if (!regie || st.simState !== 'running' || st.possessed >= 0) return false;
    S.t = getGameTime(); S.front = front; S.cur = ctx.camCur;
    if (front) { S.center.x = front.cx; S.center.z = front.cz; S.center.ext = front.ext; }
    const r = director.update(dt, S);
    Object.assign(camGoal, r.pose);
    if (r.cut) Object.assign(ctx.camCur, r.pose);
    if (r.shot !== shotShown) { shotShown = r.shot; caption('Regie · ' + r.shot.label, 2200); }
    return true;
  }

  const api = {
    setTimeScale: v => setUser(v),
    getTimeScale: () => user,
    getEffectiveTimeScale: () => desired(),
    getGameTime, rate,
    /** game clock in ms for client effects (stands still in pause, runs at the time scale) */
    now: () => clockMs,
    setDirector, isDirecting: () => regie && ctx.state().kino,
    getShot: () => { const s = director.shot; return s ? { type: s.type, label: s.label, subject: s.subject ? { ...s.subject } : null } : null; },
    /** cut the director camera to a highlight (turns the director on) */
    focusHighlight(h) { if (!regie) setDirector(true); S.t = getGameTime(); S.front = front; S.cur = ctx.camCur; director.focus(h, S); },
    getHighlights: opt => log.list(opt),
    onHighlight(cb) { listeners.add(cb); return () => listeners.delete(cb); },
    setBulletTime, isBulletTime: () => btOn,
    triggerBulletTime(label = 'Bullet-Time') { btUntil = -1e9; maybeBullet({ label }); },
    setLockstep, isLocked: () => locked,
    // hooks for main.js
    frame, onFrame, onCap, direct,
  };
  instance = api;
  return api;
}
